import { useState, useRef, useEffect } from 'react';
import { Box, Tooltip } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/edit.svg';
import { ReactComponent as CloseIcon } from '@elementary/common-ui/assets/close-2.svg';
import { ReactComponent as UndoIcon } from '@elementary/common-ui/assets/16x16/undo.svg';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import { Flex, FlexColumn } from '../../../Flex';
import TextField from '../../../TextField';
import IconButton from '../../../IconButton';
import Dialog from '../../../Dialog';
import useOnClickOutside from '../../../../hooks/useOnClickOutside';

const ApproveIconStyled = styled(VIcon)`
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const CloseIconStyled = styled(CloseIcon)`
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const StyledIconButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
`;

const RuleFieldContainer = styled(Box)`
  position: relative;
`;

const RuleFieldButtons = styled(FlexColumn)`
  position: absolute;
  bottom: 0px;
  right: 0px;
  padding: 4px;
  align-items: flex-end;
  justify-content: space-between;
  height: 100%;
`;

const StyledTextField = styled(TextField)<{ $isEditing?: boolean }>`
  background: ${({ $isEditing, theme }) =>
    $isEditing ? theme.palette.common.gray15 : theme.palette.common.white};

  .MuiOutlinedInput-input {
    padding-right: 40px !important;
  }
`;

type RuleFieldProps = {
  value?: string;
  onSave: (val: string) => void;
  onDelete?: () => void;
  editOnLoad?: boolean;
  placeholder: string;
};

const RuleField = ({
  value = '',
  onSave,
  onDelete,
  editOnLoad = false,
  placeholder,
}: RuleFieldProps) => {
  const [isEditing, setIsEditing] = useState(editOnLoad);
  const [fieldValue, setFieldValue] = useState(value);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const fieldContainerRef = useRef<HTMLInputElement>(null);

  const isEmpty = !fieldValue?.trim();
  const saveEnabled = !isEmpty && isEditing;
  const editEnabled = !isEditing && !isEmpty;
  const deleteEnabled = !!onDelete || !isEmpty;
  const undoEnabled = history.length > 0;

  const handleSave = () => {
    if (saveEnabled) {
      if (fieldValue.trim()) {
        setHistory((prev) => [...prev, value]);
      }
      onSave(fieldValue!);
      setIsEditing(!isEditing);
    }
  };

  const handleEdit = () => {
    textFieldRef.current?.focus();
    setIsEditing(!isEditing);
  };

  const handleUndo = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (history.length > 0) {
      const previousValue = history[history.length - 1];
      setFieldValue(previousValue);
      setHistory((prev) => prev.slice(0, -1));
      setIsEditing(false);
    }
    e.stopPropagation();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    } else {
      setFieldValue('');
    }
    setShowDeleteConfirm(false);
  };

  const handleClickOutside = () => {
    if (isEmpty) {
      setFieldValue(value);
    } else if (value !== fieldValue) {
      handleSave();
    }
    setIsEditing(false);
  };

  useOnClickOutside(fieldContainerRef, () => handleClickOutside());

  useEffect(() => {
    if (!value) {
      textFieldRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <RuleFieldContainer
        ref={fieldContainerRef}
        onClick={() => !isEditing && handleEdit()}
      >
        <StyledTextField
          inputRef={textFieldRef}
          fullWidth
          multiline
          $isEditing={isEditing}
          value={fieldValue}
          placeholder={placeholder}
          maxRows={isEditing ? 5 : 2}
          readOnly={!isEditing}
          onChange={(e) => setFieldValue(e.target.value)}
        />
        <RuleFieldButtons>
          {deleteEnabled ? (
            <Tooltip title="Delete" placement="top">
              <StyledIconButton onClick={() => setShowDeleteConfirm(true)}>
                <CloseIconStyled width={12} />
              </StyledIconButton>
            </Tooltip>
          ) : (
            <div />
          )}

          <Flex>
            {undoEnabled && (
              <Tooltip title="Undo" placement="bottom">
                <StyledIconButton onClick={handleUndo}>
                  <UndoIcon />
                </StyledIconButton>
              </Tooltip>
            )}

            {saveEnabled && (
              <Tooltip title="Save" placement="bottom">
                <div>
                  <StyledIconButton
                    onClick={handleSave}
                    disabled={value === fieldValue}
                  >
                    <ApproveIconStyled width={12} />
                  </StyledIconButton>
                </div>
              </Tooltip>
            )}

            {editEnabled && (
              <Tooltip title="Edit" placement="bottom">
                <StyledIconButton onClick={handleEdit}>
                  <EditIcon width={12} />
                </StyledIconButton>
              </Tooltip>
            )}
          </Flex>
        </RuleFieldButtons>
      </RuleFieldContainer>

      <Dialog
        open={showDeleteConfirm}
        titleIcon={<TrashIcon />}
        title="Delete Rule"
        message="Are you sure you want to delete this rule?"
        confirmText="Delete"
        onClose={() => setShowDeleteConfirm(false)}
        onConfirmButtonClick={handleDelete}
        isLoading={false}
      />
    </>
  );
};

export default RuleField;
